import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { OmitStrict } from "@amx/ux-twoui-utils";
import { Callout as FluentCallout, ICalloutContentStyles } from "@fluentui/react";
import React, { useEffect } from "react";
import { useState } from "react";
import { useRef } from "react";
import { AnchorPosition, anchorPositionToFluentMapping } from "../../shared/constants";
import { useComponentLogger } from "../../shared/logging";
import { BaseProps, validateProps } from "../../shared/props";
import { Button, ButtonProps } from "../Buttons/Button/Button";
import { IconButton, IconButtonProps } from "../Buttons/IconButton/IconButton";
import { NoHrefLink, NoHrefLinkProps } from "../Links/NoHrefLink/NoHrefLink";

/** role types that we allow for Callout */
export type CalloutRole = "alertdialog" | "status";

/** Styles necessary for the div wrapping the Callout's button */
const calloutWrapperStyles = {
    display: "inline-block",
};

/** Styles for the content of the Callout */
const calloutStyles = (theme: Theme): Partial<ICalloutContentStyles> => ({
    calloutMain: {
        backgroundColor: theme.palette.neutralHigh,
        maxWidth: "300px",
        padding: "20px 24px"
    },
    beak: {
        backgroundColor: theme.palette.neutralHigh
    }
});

/** Base props required by all Callouts */
export interface CalloutBaseProps extends BaseProps {
    /** Position of the Callout - see AnchorPosition */
    readonly calloutPosition?: AnchorPosition;

    /** role of the Callout, for screen readers */
    readonly calloutRole?: CalloutRole;

    /** Content to displayed inside callout */
    readonly children?: React.ReactNode;
}

/** Props for the IconButton Callout Trigger */
export interface CalloutIconButtonProps extends CalloutBaseProps {
    readonly calloutTrigger: "IconButton";
    readonly calloutTriggerProps: OmitStrict<IconButtonProps, "onClick">;
}

/** Props for the Button Callout Trigger */
export interface CalloutButtonProps extends CalloutBaseProps {
    readonly calloutTrigger: "Button";
    readonly calloutTriggerProps: OmitStrict<ButtonProps, "onClick">;
}

/** Props for the NoHrefLink Callout Trigger */
export interface CalloutNoHrefLinkProps extends CalloutBaseProps {
    readonly calloutTrigger: "NoHrefLink";
    readonly calloutTriggerProps: OmitStrict<NoHrefLinkProps, "onClick">;
}

export type CalloutProps = CalloutIconButtonProps | CalloutButtonProps | CalloutNoHrefLinkProps;

export const Callout: React.FC<CalloutProps> = ({
    calloutPosition,
    calloutRole = "status",
    children,
    ...otherProps
}) => {
    useComponentLogger("Callout", otherProps.id);
    validateProps("Callout", otherProps);

    const [isCalloutVisible, setCalloutVisible] = useState(false);
    const calloutTargetRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const onCalloutClicked = () => {
        setCalloutVisible(!isCalloutVisible);
    };

    const onCalloutDismiss = () => {
        setCalloutVisible(false);
    };

    const iconName = otherProps.calloutTrigger === "IconButton" ? otherProps.calloutTriggerProps.iconName : undefined;

    useEffect(() => {
        setCalloutVisible(false);
    }, [iconName]);

    let calloutTrigger: JSX.Element;
    switch (otherProps.calloutTrigger) {
        case "NoHrefLink":
            calloutTrigger = (
                <NoHrefLink
                    {...otherProps.calloutTriggerProps}
                    onClick={onCalloutClicked}
                />
            );
            break;
        case "IconButton":
            calloutTrigger = (
                <IconButton
                    {...otherProps.calloutTriggerProps}
                    onClick={onCalloutClicked}
                />
            );
            break;
        case "Button":
            calloutTrigger = (
                <Button
                    {...otherProps.calloutTriggerProps}
                    onClick={onCalloutClicked}
                />
            );
            break;
    }

    return (
        <>
            <div style={{ ...calloutWrapperStyles }} ref={calloutTargetRef}>
                {calloutTrigger}
            </div>
            {
                isCalloutVisible && (
                    <FluentCallout
                        styles={calloutStyles(theme)}
                        role={calloutRole}
                        target={calloutTargetRef}
                        onDismiss={onCalloutDismiss}
                        setInitialFocus={true}
                        directionalHint={anchorPositionToFluentMapping[calloutPosition || "bottom-center"]}
                        layerProps={{
                            eventBubblingEnabled: true
                        }}
                        {...otherProps}
                    >
                        {children}
                    </FluentCallout>
                )
            }
        </>
    );
};
